// Life insurance journey.

/** @type {import("../../form/types").JourneyConfig} */
const life = {
  key: "life",
  label: "Life Insurance",

  // The exact value redux stores in `insuranceType`; the route guard
  // restores it from here after a refresh or a deep link.
  insuranceType: "Life Insurance",
  entry: "/life-landing",
  landing: "landing:life",
  caseIdPrefix: "L",

  rail: {
    greeting: "Hi there",
    image: "/assets/images/cartoon-life.png",
    bullets: [
      "Instant policies online",
      "Flexible payment options",
      "Insurers you can trust",
      "24/7 claims support",
    ],
  },

  steps: [
    {
      key: "about-you",
      summary: ["life_insurance_type", "sum_insured"],
      label: "About you",
      path: "/yourself",
      mobileTitle: "About you",
      rail: {
        title: "tell us what you're",
        accent: "protecting",
        subtitle: "Your family situation and goals shape the cover we recommend.",
      },
      cta: "Continue",
      form: {
        id: "life-about",
        sections: [
          {
            fields: [
              {
                name: "life_insurance_type",
                type: "choice",
                label: "What kind of cover are you after?",
                required: true,
                col: 12,
                options: {
                  source: "reference",
                  domain: "life-insurance-type",
                  // No portrait artwork exists for life cover types, and the
                  // benefit icons that do exist are landscape line art with
                  // text baked in — unreadable as a card. So these stay as
                  // plain cards and earn their weight from the hover reveal.
                  decorate: {
                    TERM: {
                      badge: "Best value",
                      perks: [
                        "Lowest premium for the same cover",
                        "Fixed term you choose",
                        "Pays a lump sum to your family",
                      ],
                    },
                    WHOLE: {
                      perks: [
                        "Cover that never expires",
                        "Guaranteed payout whenever it happens",
                        "Useful for estate planning",
                      ],
                    },
                    SAVINGS: {
                      perks: [
                        "Protection plus an investment pot",
                        "Build a lump sum you can draw on",
                        "Premiums split between cover and savings",
                      ],
                    },
                  },
                },
              },
              {
                name: "marital_status",
                type: "select",
                label: "Marital status",
                required: true,
                options: { source: "reference", domain: "marital-status" },
              },
              {
                name: "number_of_children",
                type: "number",
                label: "Number of children",
                help: "Children who depend on you financially",
                required: true,
                rules: [{ rule: "range", min: 0, max: 20 }],
              },
              {
                name: "purpose_of_insurance",
                type: "select",
                label: "What is the cover for?",
                required: true,
                options: { source: "reference", domain: "life-purpose" },
              },
              {
                name: "plan_tenure",
                type: "select",
                label: "How long do you want cover for?",
                required: true,
                options: { source: "reference", domain: "plan-tenure" },
              },
              {
                name: "sum_insured",
                type: "currency",
                label: "Sum insured",
                required: true,
                help: "The amount paid out to your beneficiaries",
                rules: [{ rule: "range", min: 50000 }],
              },
            ],
          },
        ],
      },
    },

    {
      key: "documents",
      label: "Documents",
      path: "/document-upload",
      screen: "document-upload",
      mobileTitle: "Your documents",
      rail: {
        title: "upload your",
        accent: "documents",
        subtitle:
          "We read your Emirates ID automatically so you don't have to type it in.",
      },
      cta: "Continue to your health details",
    },

    {
      key: "health-lifestyle",
      summary: ["full_name", "is_smoker"],
      label: "Health & lifestyle",
      path: "/additional-yourself",
      // No life carrier is live yet, so the intake hands off to the holding
      // page rather than dead-ending on the last step.
      next: "/coming-soon?product=life",
      mobileTitle: "Health & lifestyle",
      rail: {
        title: "a little about",
        accent: "your health",
        subtitle: "Underwriters use this to price your cover accurately.",
      },
      cta: "See your quotes",
      form: {
        id: "life-health",
        sections: [
          {
            fields: [
              {
                name: "full_name",
                type: "text",
                label: "Full name",
                help: "Exactly as printed on your Emirates ID",
                required: true,
                ocrKeys: "Name",
              },
              {
                name: "nationality",
                type: "select",
                label: "Nationality",
                required: true,
                options: { source: "reference", domain: "nationality" },
                ocrKeys: "Nationality",
              },
              {
                name: "gender",
                type: "select",
                label: "Gender",
                required: true,
                options: { source: "reference", domain: "gender" },
                ocrKeys: "Sex",
              },
              { name: "email", type: "email", label: "Email", required: true, rules: ["email"], placeholder: "name@example.com", help: "Your policy documents are sent here" },
              { name: "mobile", type: "phone", label: "Mobile number", required: true, rules: ["uaeMobile"] },
              {
                name: "is_smoker",
                type: "radio",
                label: "Do you smoke?",
                required: true,
                col: 12,
                help: "This includes vaping and any tobacco use in the last 12 months",
                options: [
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ],
              },
            ],
          },
        ],
      },
    },
  ],
};

export default life;
